import { useCallback, useEffect, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useForm } from "react-hook-form";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { zodResolver } from "@hookform/resolvers/zod";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { TreinamentoFuncionarioForm } from "../../../rh/presentation/schemas/TreinamentoFuncionario_cadastro/treinamento_funcionario";
import { defaultReportTreinamentoFuncionarioValues, ReportTreinamentoFuncionarioForm, ReportTreinamentoFuncionarioSchema } from "../../../rh/presentation/schemas/TreinamentoFuncionario_cadastro/report_treinamento_funcionario";
import { TreinamentoFuncionario } from "../../../rh/domain/models/treinamento_funcionario";
import { useTreinamentoFuncionarioCadastroFilters } from "../../../rh/presentation/hooks/TreinamentoFuncionario_cadastro/filters";

/**
 * LoadTreinamentoFuncionariosState
 */

type LoadTreinamentoFuncionariosState = {
  cargosIds?: number[] | null;
  centroCustoIds?: number[] | null;
  FornecedorPesquisa?: number;
};

/**
 * useTreinamentoFuncionarioCadastroViewModel
 */

export const useTreinamentoFuncionarioCadastroViewModel = () => {
  const {
    commons: {
      getCargosDropDownUseCase,
      getCentroCustoDropDownUseCase,
    },
    reports: {
      getReportTreinamentoFuncionarioTercUseCase,
    },   
    terceirizacao:{getAllTreinamentoFuncionarioUseCase,
      saveTreinamentoFuncionarioUseCase,
      getAllTreinamentoFuncionarioTercDataUseCase,
      getAllTreinamentoFuncionarioTercInstrutorUseCase,
      getAllTreinamentoFuncionarioTercLocalUseCase,
    },
    rh: {
      getTreinamentosDropDownUseCase,
      
      getAllTreinamentoFuncionarioDataUseCase,
      getAllTreinamentoFuncionarioAssinaturaUseCase,
      getAllTreinamentoFuncionarioInstrutorUseCase,
      getAllTreinamentoFuncionarioLocalUseCase,
    },
  } = useDependencyInjection();

  const debouncedRef = useCreateDebounceHandler(750);

  const { setPageSaving, pushErrorNotification, pushInfoNotification } = usePageLoader();

  const hookForm = useForm<TreinamentoFuncionarioForm>();
  const hookFormReport = useForm<ReportTreinamentoFuncionarioForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: defaultReportTreinamentoFuncionarioValues,
    resolver: zodResolver(ReportTreinamentoFuncionarioSchema),
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [openReportListaTreinamentoModal, setOpenReportListaTreinamentoModal] = useState(false);

  const [leftList, setLeftList] = useState<TreinamentoFuncionario[]>([]);
  const [rightList, setRightList] = useState<TreinamentoFuncionario[]>([]);

  const filters = useTreinamentoFuncionarioCadastroFilters();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  const [dataRealizacao, instrutor] = hookFormReport.watch(['dataRealizacao', 'instrutor']);

  /**
   * Funcoes de carregamento
   */

  const loadTreinamentosDropDown = useCallback(async () => {
    try {
      const treinamentos = await getTreinamentosDropDownUseCase.getTreinamentosDropDown();
      useTreinamentoFuncionarioCadastroFilters.getState().setTreinamentos(treinamentos);
    } catch (e: unknown) {
      throw e;
    }
  }, [getTreinamentosDropDownUseCase]);

  const loadCargosDropDown = useCallback(async () => {
    try {
      const cargos = await getCargosDropDownUseCase.getCargosDropDown();
      useTreinamentoFuncionarioCadastroFilters.getState().setCargos(cargos);
    } catch (e: unknown) {
      throw e;
    }
  }, [getCargosDropDownUseCase]);

  const loadCentroCustosDropDown = useCallback(async () => {
    try {
      const centroCustos = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      useTreinamentoFuncionarioCadastroFilters.getState().setCentroCustos(centroCustos);
    } catch (e: unknown) {
      throw e;
    }
  }, [getCentroCustoDropDownUseCase]);

  const loadTreinamentoFuncionarios = useCallback(async (funcState?: LoadTreinamentoFuncionariosState) => {
    try {
      const filteredCargos = evaluateParamValue(funcState?.cargosIds, filters.selectedCargoIds);
      const filteredCentroCustos = evaluateParamValue(funcState?.centroCustoIds, filters.selectedCentroCustoIds);
      const filteredFornecedorPesquisa = funcState?.FornecedorPesquisa ?? filters.FornecedorPesquisa;

      const treinamentoFuncionarios = await getAllTreinamentoFuncionarioUseCase.getAllTreinamentoFuncionario(
        filteredCargos ?? [],
        filteredCentroCustos ?? [],
        filteredFornecedorPesquisa
      );

      setLeftList([...treinamentoFuncionarios]);
      setRightList([]);
    } catch (e: unknown) {
      setLeftList([]);
      setRightList([]);

      throw e;
    }
  }, [ filters.FornecedorPesquisa, filters.selectedCargoIds, filters.selectedCentroCustoIds, getAllTreinamentoFuncionarioUseCase]);

  const loadTreinamentoFuncionarioData = useCallback(async () => {
    try {
      const datas = await getAllTreinamentoFuncionarioTercDataUseCase.getAllTreinamentoFuncionarioTercData();
      useTreinamentoFuncionarioCadastroFilters.getState().setDatas(datas);
    } catch (e: unknown) {
      useTreinamentoFuncionarioCadastroFilters.getState().setDatas([]);
      throw e;
    }
  }, [getAllTreinamentoFuncionarioTercDataUseCase]);


  const loadTreinamentoFuncionarioInstrutores = useCallback(async (data: Date) => {
    try {
      const instrutores = await getAllTreinamentoFuncionarioTercInstrutorUseCase
        .getAllTreinamentoFuncionarioTercInstrutor(data);
      useTreinamentoFuncionarioCadastroFilters.getState().setInstrutores(instrutores);
    } catch (e: unknown) {
      useTreinamentoFuncionarioCadastroFilters.getState().setInstrutores([]);
      throw e;
    }
  }, [getAllTreinamentoFuncionarioTercInstrutorUseCase]);

  const loadTreinamentoFuncionarioLocais = useCallback(async (data: Date, instrutor: string) => {
    try {
      const locais = await getAllTreinamentoFuncionarioTercLocalUseCase
        .getAllTreinamentoFuncionarioTercLocal(data, instrutor);
      useTreinamentoFuncionarioCadastroFilters.getState().setLocais(locais);
    } catch (e: unknown) {
      useTreinamentoFuncionarioCadastroFilters.getState().setLocais([]);
      throw e;
    }
  }, [getAllTreinamentoFuncionarioTercLocalUseCase]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined);
      await loadTreinamentosDropDown();
      await loadCargosDropDown();
      await loadCentroCustosDropDown();
      await loadTreinamentoFuncionarios();
      await loadTreinamentoFuncionarioData();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao iniciar pagina!")[0]);
    }
  });

  useEffect(() => {
    (async function () {
      try {
        if (dataRealizacao) {
          await loadTreinamentoFuncionarioInstrutores(dataRealizacao);
        } else {
          useTreinamentoFuncionarioCadastroFilters.getState().setInstrutores([]);
          useTreinamentoFuncionarioCadastroFilters.getState().setLocais([]);
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar instrutores!'));
      }
    })();
  }, [
    dataRealizacao,
    loadTreinamentoFuncionarioInstrutores,
    pushErrorNotification,
  ]);

  useEffect(() => {
    (async function () {
      try {
        if (dataRealizacao && instrutor) {
          await loadTreinamentoFuncionarioLocais(dataRealizacao, instrutor);
        } else {
          useTreinamentoFuncionarioCadastroFilters.getState().setLocais([]);
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar instrutores!'));
      }
    })();
  }, [
    dataRealizacao,
    instrutor,
    loadTreinamentoFuncionarioLocais,
    pushErrorNotification,
  ]);

  /**
   * Eventos
   */

  const handleReloadPage = useCallback(async () => {
    try {
      setLoading(true);
      await loadTreinamentosDropDown();
      await loadCargosDropDown();
      await loadCentroCustosDropDown();
      await loadTreinamentoFuncionarios();
      await loadTreinamentoFuncionarioData();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao iniciar pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadTreinamentosDropDown,
    loadCargosDropDown,
    loadCentroCustosDropDown,
    loadTreinamentoFuncionarios,
    loadTreinamentoFuncionarioData,
    pushErrorNotification,
  ]);

  const handleSubmit = useCallback(async (form: TreinamentoFuncionarioForm) => {
    if (!saving || !loading) {
      try {
        setLoading(true);
        setPageSaving(true);

        await saveTreinamentoFuncionarioUseCase.saveTreinamentoFuncionario(
          form.treinamentoIds,
          form.funcionarioIds,
          form.dataRealizacao,
          form.local,
          form.instrutor,
          form.prazoAvaliacao,
        );

        await loadTreinamentoFuncionarios();
        await loadTreinamentoFuncionarioData();

        pushInfoNotification("Sucesso", "Alterações salvas com sucesso!", { isAutoFinish: true });

        hookForm.reset();
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao iniciar pagina!"));
      } finally {
        setLoading(false);
        setSaving(false);
        setPageSaving(false);
      }
    }
  }, [saving, loading, setPageSaving, saveTreinamentoFuncionarioUseCase, loadTreinamentoFuncionarios, loadTreinamentoFuncionarioData, pushInfoNotification, hookForm, pushErrorNotification]);

  const handleSubmitReport = useCallback(async (form: ReportTreinamentoFuncionarioForm) => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportTreinamentoFuncionarioTercUseCase.getReportTreinamentoFuncionarioTerc(
            requiredMimeType,
            form.dataRealizacao,
            form.local,
            form.instrutor,
            form.conteudoProgramatico,
          ),
        closeReportModalCallback: () => {
          setOpenReportListaTreinamentoModal(false);
        }
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao gerar relatorio"));
    } finally {
      setLoading(false);
    }
  }, [startReportViewerProcess, getReportTreinamentoFuncionarioTercUseCase, pushErrorNotification]);

  const handleFuncionarioSelectionChange = useCallback((src: TreinamentoFuncionario[], dst: TreinamentoFuncionario[]) => {
    setLeftList(src);
    setRightList(dst);

    hookForm.setValue('funcionarioIds', dst.map(x => x.idFuncionario));
  }, [
    hookForm,
  ]);

  const handleSelectedCargosChange = useDebouncedCallback(debouncedRef, (cargos: number[] | null) => {
    useTreinamentoFuncionarioCadastroFilters.getState().setSelectedCargoIds(cargos);
    return async () => {
      try {
        setLoading(true);
        await loadTreinamentoFuncionarios({ cargosIds: cargos });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao iniciar pagina!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadTreinamentoFuncionarios,
    pushErrorNotification,
  ]);

  const handleSelectedCentroCustosChange = useDebouncedCallback(debouncedRef, (centroCustos: number[] | null) => {
    useTreinamentoFuncionarioCadastroFilters.getState().setSelectedCentroCustoIds(centroCustos);
    return async () => {
      try {
        setLoading(true);
        await loadTreinamentoFuncionarios({ centroCustoIds: centroCustos });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao iniciar pagina!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadTreinamentoFuncionarios, pushErrorNotification]);

  const handleFornecedorChange = useDebouncedCallback(debouncedRef, async (newValue: number) => {
    useTreinamentoFuncionarioCadastroFilters.getState().setFornecedorPesquisa(newValue);

    try {
      setLoading(true);
      await loadTreinamentoFuncionarios({ FornecedorPesquisa: newValue, page: 1 } as any);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionarios!"));
    } finally {
      setLoading(false);
    }

    return () => {};
  }, [pushErrorNotification, loadTreinamentoFuncionarios]);
    
  

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);

      useTreinamentoFuncionarioCadastroFilters.getState().setSelectedCargoIds(null);
      useTreinamentoFuncionarioCadastroFilters.getState().setSelectedCentroCustoIds(null);

      await loadTreinamentoFuncionarios({ cargosIds: null, centroCustoIds: null });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao iniciar pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadTreinamentoFuncionarios,
    pushErrorNotification,
  ]);

  const handleReportModalOpenState = useCallback((openState: boolean) => {
    setOpenReportListaTreinamentoModal(openState);
  }, []);

  return {
    hookForm,
    hookFormReport,
    loading,
    saving,
    errorMessage,
    openReportListaTreinamentoModal,
    leftList: leftList.sort((a, b) => a.nome.toUpperCase().localeCompare(b.nome.toUpperCase())),
    rightList: rightList.sort((a, b) => a.nome.toUpperCase().localeCompare(b.nome.toUpperCase())),
    filters,
    reportViewerState,
    handleReloadPage,
    handleSubmit: hookForm.handleSubmit(handleSubmit),
    handleSubmitReport,
    handleFuncionarioSelectionChange,
    handleSelectedCargosChange,
    handleSelectedCentroCustosChange,
    handleClearFilters,
    handleReportModalOpenState,
    handleFornecedorChange
  };
}